/* SoFly store: preserve the Shopify cart while Accounts authenticates. */
(() => {
  'use strict';
  const theme = '191169790280';
  if (!document.querySelector(`[data-turbine-checkout="${theme}"]`) || location.origin !== 'https://sofly.io') return;
  const key = `sofly-checkout:${theme}`;
  const root = window.Shopify?.routes?.root || '/';
  const cartKey = `${key}:connection`;
  let cartConnecting = false;
  function connectCart() {
    if (cartConnecting) return;
    if (!/^\/(?:[a-z]{2}(?:-[A-Za-z]{2})?\/)?$/.test(root)) throw new Error('Your cart is unavailable.');
    const state = Array.from(crypto.getRandomValues(new Uint8Array(32)), byte => byte.toString(16).padStart(2, '0')).join('');
    sessionStorage.setItem(cartKey, JSON.stringify({state, at: Date.now()}));
    cartConnecting = true;
    notice('Connecting to your SoFly account…');
    location.assign(`https://account.sofly.io/store/cart?${new URLSearchParams({theme, state, locale: root})}`);
  }
  let clubChoice;
  let clubPersisted = false;
  let clubVersion = 0;
  let clubSaving = Promise.resolve();
  const clubNotice = text => document.querySelectorAll('[data-social-club-status]').forEach(el => { el.textContent = text; });
  document.addEventListener('change', event => {
    if (!event.target.matches?.('[data-social-club-opt-in]')) return;
    const selected = event.target.checked;
    const version = ++clubVersion;
    clubChoice = selected;
    clubPersisted = false;
    document.querySelectorAll('[data-social-club-opt-in]').forEach(input => { input.checked = selected; });
    clubNotice('Saving your Social Club choice…');
    clubSaving = clubSaving.catch(() => {}).then(async () => {
      try {
        const protection = await fetch('https://account.sofly.io/store/checkout/csrf', {credentials: 'include', headers: {Accept: 'application/json'}});
        if (!protection.ok) throw new Error('Unavailable');
        const csrf = await protection.json();
        const response = await fetch('https://account.sofly.io/store/social-club', {
          method: 'POST', credentials: 'include', headers: {'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json'},
          body: new URLSearchParams({subscribed: selected ? '1' : '0', _token: csrf.token}).toString()
        });
        if (!response.ok) throw new Error('Sign-in needed');
        const result = await response.json();
        if (result.saved !== true || result.subscribed !== selected) throw new Error('Choice was not saved');
        if (version !== clubVersion) return;
        clubPersisted = result.saved === true;
        clubNotice(result.pending ? 'Your Social Club choice is saved.' : (selected ? 'You’re signed up to Social Club news and offers.' : 'You’re unsubscribed from Social Club news and offers.'));
      } catch {
        if (version === clubVersion) clubNotice('Your choice will be saved when you sign in and continue to checkout.');
      }
    });
  });
  const token = cart => String(cart.token || '').split('?')[0];
  const fingerprint = cart => JSON.stringify((cart.items || []).map(item => [
    String(item.variant_id), item.quantity, item.selling_plan_allocation?.selling_plan?.id || null,
    Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b))
  ]).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))));
  const notice = message => document.querySelectorAll('sofly-digital-consent [role="status"]').forEach(el => { el.textContent = message; });
  const cartRequest = async (path, options) => {
    const response = await fetch(`${root}${path}`, { credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, ...options });
    if (!response.ok) throw new Error('Your cart could not be checked.');
    return response.json();
  };
  window.soflyCheckout = Object.freeze({
    socialClubChoice() { return clubChoice; },
    async begin(cart) {
      if (cartConnecting) throw new Error('Your SoFly account is connecting. Please wait.');
      if (!token(cart) || !cart.items?.length || !/^\/(?:[a-z]{2}(?:-[A-Za-z]{2})?\/)?$/.test(root)) throw new Error('Your cart is unavailable.');
      const bytes = crypto.getRandomValues(new Uint8Array(32));
      const state = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
      // Storage is required: do not redirect if the browser cannot bind return state.
      sessionStorage.setItem(key, JSON.stringify({ state, cart: token(cart), fingerprint: fingerprint(cart), at: Date.now() }));
      const protection = await fetch('https://account.sofly.io/store/checkout/csrf', { credentials: 'include', headers: { Accept: 'application/json' } });
      if (!protection.ok) throw new Error('Account checkout is unavailable.');
      const csrf = await protection.json();
      if (typeof csrf.token !== 'string' || !/^[A-Za-z0-9]{40}$/.test(csrf.token)) throw new Error('Account checkout could not be secured.');
      const form = document.createElement('form');
      form.method = 'POST'; form.action = 'https://account.sofly.io/store/checkout';
      form.hidden = true;
      await clubSaving;
      const fields = { theme, cart: token(cart), state, locale: root, policy_version: '1.0.5', digital_supply: '1', withdrawal_acknowledgement: '1', _token: csrf.token };
      if (clubChoice !== undefined && !clubPersisted) fields.social_club = clubChoice ? '1' : '0';
      for (const [name, value] of Object.entries(fields)) {
        const input = document.createElement('input'); input.type = 'hidden'; input.name = name; input.value = value; form.append(input);
      }
      document.body.append(form); form.submit();
    }
  });
  async function resume() {
    const match = location.hash.match(/^#sofly-checkout=([a-f0-9]{64})\.([a-f0-9]{64})$/);
    if (!match) {
      if (location.pathname !== `${root}cart` && location.pathname !== `${root}cart/`) return;
      try {
        const connected = location.hash.match(/^#sofly-cart=([a-f0-9]{64})$/);
        const pending = JSON.parse(sessionStorage.getItem(cartKey) || 'null');
        sessionStorage.removeItem(cartKey);
        if (connected) history.replaceState(null, '', location.pathname + location.search);
        if (connected && pending?.state === connected[1] && pending.at <= Date.now() && Date.now() - pending.at < 3600000) {
          notice('Your SoFly account is connected.');
          return;
        }
        connectCart();
      } catch {
        cartConnecting = true;
        notice('Your account connection could not be secured. Enable browser session storage and open your cart again.');
      }
      return;
    }
    // Strip the one-use handoff from history before loading more resources.
    history.replaceState(null, '', location.pathname + location.search);
    try {
      const pending = JSON.parse(sessionStorage.getItem(key) || 'null');
      sessionStorage.removeItem(key);
      if (!pending || pending.state !== match[2] || Date.now() - pending.at > 3600000 || pending.at > Date.now()) throw new Error('Checkout expired. Please tick the box and check out again.');
      notice('Your account is connected. Preparing checkout…');
      const cart = await cartRequest('cart.js');
      if (!cart.items?.length || token(cart) !== pending.cart || fingerprint(cart) !== pending.fingerprint) throw new Error('Your cart changed. Please review it, tick the box and check out again.');
      if (document.querySelector('[name="checkout"][data-release-blocked="true"]')) throw new Error('Remove the unreleased product before checking out.');
      const saved = await cartRequest('cart/update.js', { method: 'POST', body: JSON.stringify({ attributes: { __sofly_checkout: match[1], 'Digital supply request v1.0.0': 'Requested', 'Withdrawal acknowledgement v1.0.0': 'Acknowledged' } }) });
      if (token(saved) !== pending.cart || fingerprint(saved) !== pending.fingerprint) throw new Error('Your cart changed. Please review it and check out again.');
      const protection = await fetch('https://account.sofly.io/store/checkout/csrf', { credentials: 'include', headers: { Accept: 'application/json' } });
      if (!protection.ok) throw new Error('Your account contact details could not be checked. Please try checkout again.');
      const csrf = await protection.json();
      const contactResponse = await fetch('https://account.sofly.io/store/checkout/contact', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
        body: new URLSearchParams({ grant: match[1], state: match[2], cart: pending.cart, _token: csrf.token }).toString()
      });
      if (!contactResponse.ok) throw new Error('Your account contact details could not be checked. Please try checkout again.');
      const contact = await contactResponse.json();
      if (typeof contact.email !== 'string' || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) throw new Error('Your account needs a valid contact email before checkout.');
      const parameters = new URLSearchParams({ 'checkout[email]': contact.email });
      const fields = { firstName: 'first_name', lastName: 'last_name', address1: 'address1', address2: 'address2', city: 'city', company: 'company', countryCode: 'country', provinceCode: 'province', zip: 'zip' };
      for (const [field, name] of Object.entries(fields)) {
        const value = contact.address?.[field];
        if (typeof value !== 'string' || !value.trim()) continue;
        parameters.set(`checkout[shipping_address][${name}]`, value);
      }
      location.assign(`${root}cart/checkout?${parameters}`);
    } catch (error) {
      notice(error.message || 'We could not prepare checkout. Please try again.');
    }
  }
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank' || link.hasAttribute('download')) return;
    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin || ![`${root}cart`, `${root}cart/`].includes(url.pathname)) return;
    // The slide-in drawer remains a quick cart preview. Its checkout still
    // uses the separately authenticated, cart-bound checkout handoff.
    if (link.id === 'cart-icon-bubble' && document.querySelector('cart-drawer')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    try { connectCart(); }
    catch { notice('Your account connection could not be secured. Enable browser session storage and open your cart again.'); }
  }, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', resume, { once: true });
  else resume();
})();
